import type { Locale } from "@/lib/i18n-config";

/**
 * Single source of truth for customer-facing proof numbers.
 *
 * Every figure here is verifiable and rounded DOWN, per the BrandScript
 * guardrails (.agents/storybrand-brandscript.md). The CV review count is live:
 * `getCvsReviewedCount` (proof-stats-server.ts) counts completed free reviews
 * and `useCvsReviewedCount` reads it on the client. The rating figures are
 * still static; as of 2026-06-25 the admin dashboard reported 631 ratings
 * averaging 4.89.
 *
 * Never reintroduce unverifiable claims like success rates or average score
 * improvements: they read as promising hiring outcomes, which the brand
 * guardrails forbid.
 */

/**
 * Fallback CV review count, shown only until the live count loads or when the
 * database is unreachable. Rounded down from the verified 2,477 (2026-06-25),
 * so a fallback can understate the live figure but never overstate it.
 */
export const CVS_REVIEWED_COUNT = 2400;

/** Proof counts are shown in whole hundreds, rounded down. */
export function roundDownProofCount(count: number): number {
  if (!Number.isFinite(count) || count <= 0) {
    return 0;
  }
  return count < 100 ? Math.floor(count) : Math.floor(count / 100) * 100;
}

/** Mean of the user ratings (4.89 across 631 ratings), displayed as 4.9. */
export const RATING_VALUE = 4.9;

/** Number of user ratings behind RATING_VALUE. */
export const RATING_COUNT = 631;

/** The free review returns in about this many minutes. */
export const FREE_REVIEW_MINUTES = 2;

const NUMBER_LOCALE: Record<Locale, string> = {
  en: "en-US",
  id: "id-ID",
};

const RATING_DISPLAY: Record<Locale, string> = {
  en: "4.9/5",
  id: "4,9/5",
};

const FREE_REVIEW_DISPLAY: Record<Locale, string> = {
  en: "2 minutes",
  id: "2 menit",
};

/**
 * Locale-formatted CV review count, e.g. "2,400+" (en) or "2.400+" (id).
 * Pass the live count; it is rounded down to whole hundreds here, so callers
 * can never display a more precise (or larger) figure than the rule allows.
 */
export function formatCvsReviewed(
  locale: Locale,
  count: number = CVS_REVIEWED_COUNT,
): string {
  const rounded = roundDownProofCount(count);
  return `${new Intl.NumberFormat(NUMBER_LOCALE[locale]).format(rounded)}+`;
}

/** Locale-formatted rating, e.g. "4.9/5" (en) or "4,9/5" (id). */
export function formatRating(locale: Locale): string {
  return RATING_DISPLAY[locale];
}

/** Locale-formatted free review turnaround, e.g. "2 minutes" or "2 menit". */
export function formatFreeReviewTime(locale: Locale): string {
  return FREE_REVIEW_DISPLAY[locale];
}
